import { gql } from 'graphql-request';
import firebase from 'firebase/app';

import {
  baseApi,
  select,
  insert,
  update,
  afterInsert,
  afterUpdate,
  afterMultipleUpdate,
  afterInsertSheetRow,
  afterSeqUpdate,
  beforeUpdate,
  beforeUpdateSheetRow,
  ws,
  objectToStr,
  splitJsonFields,
  toCacheKey,
  buildPaginationClauses,
  escapeBackslashes,
} from './base';

import {
  sheetType,
  publicSheetType,
  sheetListType,
  sheetRowType,
  commentWithSheetRowType,
  messageType,
  sheetProfileType,
  sheetRowAuditType,
  printTemplateType,
  sheetActiveUserType,
} from '../graphqlTypes';
import data from '@iconify/icons-eva/arrow-ios-upward-fill';

function onCommentUpdate(endPoint, isList) {
  return async (patch, { dispatch, queryFulfilled }) => {
    const id = patch.cacheKey;
    const patchResult = dispatch(
      baseApi.util.updateQueryData(
        patch.endPointOverride || 'getSheetRowComments',
        toCacheKey(id),
        (draft) => {
          const i = draft.findIndex((x) => x.id === patch.id);
          Object.assign(draft[i], patch.payload);
        }
      )
    );
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData(
          'getAllSheetComments',
          toCacheKey(patch.allCommentsKey),
          (draft) => {
            const i = draft.findIndex((x) => x.id === data.id);
            Object.assign(draft[i], data);
          }
        )
      );
    } catch (error) {
      patchResult.undo();
      console.log(error);
    }
  };
}

const processSheetRow = ({ custom, ...rest }) => ({
  ...rest,
  ...(custom || {}), // Spread `custom` only if it exists
});

function createCustomField(fields) {
  return Object.entries(fields).reduce(
    (acc, [key, value]) => {
      if (/^col\d+$/.test(key)) {
        acc.custom[key] = value; // Group colXX keys under "custom"
      } else {
        acc.payload[key] = value; // Keep non-colXX keys as they are
      }
      return acc;
    },
    { custom: {}, payload: {} }
  );
}

const isNotEmpty = (obj) => Object.keys(obj).length > 0;

// , {response: (response) => removeCustomFromAccessor(response.sheet_by_pk)}

const sheetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchSheets: select(builder, 'sheet', sheetType, false, {
      cacheKey: 'searchSheets(undefined)',
      query: (params = {}) => {
        const search = typeof params.search === 'string' ? params.search : '';
        const hasSearch = search.trim().length > 0;
        const cleanSearch = search.replace(/%/g, '');
        const countMatch = cleanSearch.match(/^\s*(\d+)\.?\s*$/);
        const archivedOnly =
          typeof params.archivedOnly === 'boolean' ? params.archivedOnly : undefined;
        const starredOnly = !!params.starredOnly;
        const profileId =
          (typeof params.profileId === 'number' && Number.isFinite(params.profileId)) ||
          typeof params.profileId === 'string'
            ? params.profileId
            : undefined;
        const parentOnly = params.parentOnly !== false; // default true (only top-level)
        const specProjectId =
          (typeof params.specProjectId === 'number' && Number.isFinite(params.specProjectId)) ||
          typeof params.specProjectId === 'string'
            ? params.specProjectId
            : undefined;

        const andConditions = [];

        if (typeof archivedOnly === 'boolean') {
          andConditions.push(`{ is_archived: { _eq: ${archivedOnly} } }`);
        }

        if (parentOnly) {
          andConditions.push(`{ parent_id: { _is_null: true } }`);
        }

        if (typeof specProjectId !== 'undefined') {
          const specProjectIdLiteral =
            typeof specProjectId === 'number' ? specProjectId : `"${specProjectId}"`;
          andConditions.push(`{ spec_project_id: { _eq: ${specProjectIdLiteral} } }`);
        }

        if (hasSearch) {
          const orParts = [
            `{ name: { _ilike: "${search}" } }`,
            `{ type: { _ilike: "${search}" } }`,
            `{ spec_project: { name: { _ilike: "${search}" } } }`,
            `{ revisions: { name: { _ilike: "${search}" } } }`,
          ];
          if (countMatch) {
            const revCount = Number(countMatch[1]);
            if (Number.isFinite(revCount)) {
              orParts.push(
                `{ revisions_aggregate: { count: { predicate: { _eq: ${revCount} } } } }`
              );
            }
          }
          andConditions.push(`{ _or: [ ${orParts.join(', ')} ] }`);
        }

        if (starredOnly && typeof profileId !== 'undefined') {
          const profileIdLiteral = typeof profileId === 'number' ? profileId : `"${profileId}"`;
          andConditions.push(
            `{ sheet_profiles: { is_star: { _eq: true }, profile_id: { _eq: ${profileIdLiteral} } } }`
          );
        }

        const where = andConditions.length
          ? `where: { _and: [ ${andConditions.join(', ')} ] }`
          : '';

        const order = 'order_by: { updated_at: desc }';
        const clauses = [where, order].filter(Boolean);

        const [limitClause, offsetClause] = buildPaginationClauses({
          params,
          hasSearch,
        });
        clauses.push(limitClause);
        clauses.push(offsetClause);

        const finalClauses = clauses.join(', ');

        return {
          body: gql`
            query {
              sheet(${finalClauses}) ${sheetType}
            }
          `,
        };
      },
    }),
    getProjectSheets: select(builder, 'sheet', sheetType, false, {
      filter: { specProjectId: 'spec_project_id' },
    }),
    getSheet: builder.query({
      query: (id) => ({
        body: gql`
        query {
          sheet_by_pk(id: ${id}) ${sheetType}
        }
        `,
      }),
      transformResponse: (response) => response.sheet_by_pk,
      providesTags: (result, error, id) => [{ type: 'Sheet', id }],
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const { currentUser } = firebase.auth();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          const { data: loadedSheet } = await cacheDataLoaded;
          const lastUpdated = loadedSheet.updated_at ?? '2023-01-01T00:00:00+00:00';
          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_stream(
                  batch_size: 1,
                  cursor: {initial_value: {updated_at: "${lastUpdated}"}, ordering: ASC},
                  where: {id: {_eq: ${arg}}}
                ) { id seq updated_at }
              }
              `,
            },
            {
              next: (pushed) => {
                const incoming = pushed?.data?.sheet_stream?.[0];
                if (!incoming) return;
                updateCachedData((draft) => {
                  if (JSON.stringify(draft.seq) !== JSON.stringify(incoming.seq)) {
                    draft._seqChangedByOther = true;
                  }
                });
              },
              error: (err) => console.error('sheet stream error', err),
              complete: () => {},
            }
          );
        } catch {}
        await cacheEntryRemoved;
        unsubscribe();
      },
    }),
    getPublicSheet: select(builder, 'public_sheet', publicSheetType, false, {
      filter: { id: 'id' },
      response: (response) => response.public_sheet[0],
    }),
    getLatestSheets: select(builder, 'sheet', sheetListType, false, {
      limit: 5,
      order: `{updated_at: desc}`,
    }),
    getSheets: select(builder, 'sheet', sheetListType, false, {
      filter: {
        isMaster: 'is_master',
        parentId: 'parent_id',
        isArchived: 'is_archived',
        specProjectId: 'spec_project_id',
      },
      order: `{is_master: desc, name: asc}`,
    }),
    updateSheet: update(builder, 'sheet', sheetType, beforeUpdate('getSheet')),
    copySheet: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          copy_schedule(args: ${objectToStr(params.payload)})
          ${sheetType}
        } 
        `,
      }),
      transformResponse: (response) => response.copy_schedule,
      invalidatesTags: (result, error, arg) => [{ type: 'Sheet', id: arg.id }],
    }),
    getSubHeadersCode: builder.query({
      query: (sheetId) => ({
        body: gql`
        query {
          sheet_row(where: {sheet_id: {_eq: ${sheetId}}}) ${sheetRowType}
        }
        `,
      }),
      transformResponse: (response) => response.sheet_row,
      providesTags: (result, error, arg) => [{ type: 'SheetRow', id: arg }, 'SheetRow'],
    }),
    getSheetRows: builder.query({
      query: (sheetId) => ({
        body: gql`
        query {
          ordered_sheet_row(args: {by_sheet_id: ${sheetId}}) ${sheetRowType}
        }
        `,
      }),
      transformResponse: (response) => response.ordered_sheet_row.map(processSheetRow),
      providesTags: (result, error, arg) => [{ type: 'SheetRow', id: arg }, 'SheetRow'],
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const { currentUser } = firebase.auth();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          const { data: loadedRows } = await cacheDataLoaded;
          const lastUpdated =
            [...loadedRows].sort((a, b) => (a.updated_at > b.updated_at ? -1 : 1))[0]
              ?.updated_at ?? '2023-01-01T00:00:00+00:00';

          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_row_stream(
                  batch_size: 10,
                  cursor: {initial_value: {updated_at: "${lastUpdated}"}, ordering: ASC},
                  where: {sheet_id: {_eq: ${arg}}}
                ) ${sheetRowType}
              }
              `,
            },
            {
              next: (pushed) => {
                const incoming = pushed?.data?.sheet_row_stream;
                if (!incoming?.length) return;
                updateCachedData((draft) => {
                  incoming.forEach((updated) => {
                    const idx = draft.findIndex((r) => r.id === updated.id);
                    if (idx !== -1) {
                      Object.assign(draft[idx], processSheetRow(updated));
                    }
                  });
                });
              },
              error: (err) => console.error('sheet row stream error', err),
              complete: () => {},
            }
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`
        }
        await cacheEntryRemoved;
        unsubscribe();
      },
    }),
    updateSheetRow: builder.mutation({
      query: (params) => {
        const fields = escapeBackslashes(createCustomField(params.payload));
        delete fields.payload?.custom;
        return {
          body: gql`
          mutation {
            update_sheet_row_by_pk(pk_columns: {id: ${params.id}}, 
              _set: ${objectToStr(fields.payload)}
              ${
                isNotEmpty(fields.custom)
                  ? `, _append: {custom: ${objectToStr(fields.custom)}}`
                  : ''
              }
            )  ${sheetRowType}
          }`,
        };
      },
      transformResponse: (response) => response.update_sheet_row_by_pk,
      onQueryStarted: beforeUpdateSheetRow('getSheetRows'),
    }),
    updateSheetRows: builder.mutation({
      query: (params) => {
        params = escapeBackslashes(params);
        return {
          body: gql`
          mutation {
            update_sheet_row(where: {id: {_in: [${params.ids.join(',')}]}}, 
              _set: ${objectToStr(params.payload)}
            ) {returning ${sheetRowType}}
          }`,
        };
      },
      transformResponse: (response) => response.update_sheet_row.returning.map(processSheetRow),
      onQueryStarted: afterMultipleUpdate('getSheetRows'),
    }),
    updateSheetRowFields: builder.mutation({
      query: (params) => {
        const fields = escapeBackslashes(createCustomField(params.payload));
        return {
          body: gql`
        mutation {
          update_sheet_row_by_pk(pk_columns: {id: ${params.id}}, 
            _set: ${objectToStr(fields.payload)},
            _append: ${objectToStr(fields.custom)}
          )  ${sheetRowType}
        }`,
        };
      },
      transformResponse: (response) => processSheetRow(response.update_sheet_row_by_pk),
      onQueryStarted: afterUpdate('getSheetRows'),
    }),
    updateManySheetRows: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          update_sheet_row_many (
            updates: [
              ${params.rows
                .map(
                  (r) => `
              {
                where: {id: {_eq: ${r.id}}},
                _set: ${objectToStr(r.payload)}
              }`
                )
                .join(',')}
            ]
          ) {returning ${sheetRowType}}
        }`,
      }),
      transformResponse: (response) =>
        response.update_sheet_row_many
          .reduce((acc, curr) => acc.concat(curr.returning), [])
          .map(processSheetRow),
      onQueryStarted: afterMultipleUpdate('getSheetRows'),
    }),
    insertSheetRow1: insert(builder, 'sheet_row', sheetRowType, afterInsertSheetRow()),
    insertSheetRow: builder.mutation({
      query: (params) => {
        params = escapeBackslashes(createCustomField(params.payload));
        return {
          body: gql`
              mutation {
                insert_sheet_row_one(object: 
                  ${objectToStr({ ...params.payload, custom: params.custom || {} })}
                )  ${sheetRowType}
              }
              `,
        };
      },
      transformResponse: (response) => processSheetRow(response.insert_sheet_row_one),
      onQueryStarted: afterInsertSheetRow(),
    }),
    insertSheetRows: insert(
      builder,
      'sheet_row',
      `{returning ${sheetRowType}}`,
      afterInsertSheetRow(),
      (result, error, arg) => [{ type: 'SheetRow', id: arg.payload[0].sheet_id.toString() }],
      true
    ),
    getSheetUnresolvedComments: select(
      builder,
      'sheet_row_comment',
      commentWithSheetRowType,
      false,
      {
        filter: (params) =>
          `{sheet_row: {sheet_id: {_eq: ${params.sheetId}}}, is_resolved: {_eq: false}, created_by: {_neq: ${params.profileId}}}`,
        order: `{created_at: desc}`,
      }
    ),
    getAllSheetComments: builder.query({
      query: (params) => ({
        body: gql`
        query {
          sheet_row_comment(
            where: {sheet_row: {sheet_id: {_eq: ${params.sheetId}}}}, 
            order_by: {created_at: asc}
          ) ${commentWithSheetRowType}
        }
        `,
      }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // create a websocket connection when the cache subscription starts
        const { currentUser } = firebase.auth();
        // const token = await currentUser.getIdToken();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          // wait for the initial query to resolve before proceeding
          const { data: loadedData } = await cacheDataLoaded;
          const lastMessageDate =
            loadedData[loadedData.length - 1]?.created_at || '2023-01-01T00:36:01.858631+00:00';

          const onNext = (pushed) => {
            /* handle incoming values */
            const row = pushed?.data?.sheet_row_comment_stream[0];
            updateCachedData((draft) => {
              draft.push(row);
            });
          };

          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_row_comment_stream(
                  batch_size: 1,
                  cursor: {initial_value: {created_at: "${lastMessageDate}"}, ordering: ASC},
                  where: {sheet_row: {sheet_id: {_eq: ${arg.sheetId}}}})
                ${commentWithSheetRowType}
              }
              `,
            },
            {
              next: onNext,
              error: (err) => console.error('sheet comment subscription error', err),
              complete: () => {},
            }
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        }
        await cacheEntryRemoved;
        unsubscribe();
      },
      transformResponse: (response) => response.sheet_row_comment,
      providesTags: ['SheetComment'],
    }),
    getSheetRowComments: builder.query({
      query: (params) => ({
        body: gql`
        query {
          sheet_row_comment(
            where: {sheet_row_id: {_eq: ${params.sheetRowId}}}, 
            order_by: {created_at: asc}
          ) ${messageType}
        }
        `,
      }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // create a websocket connection when the cache subscription starts
        const { currentUser } = firebase.auth();
        // const token = await currentUser.getIdToken();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          // wait for the initial query to resolve before proceeding
          const { data: loadedData } = await cacheDataLoaded;
          const lastMessageDate =
            loadedData[loadedData.length - 1]?.created_at || '2023-01-01T00:36:01.858631+00:00';

          const onNext = (pushed) => {
            /* handle incoming values */
            const row = pushed?.data?.sheet_row_comment_stream[0];
            if (row?.profile?.id !== currentUser.uid) {
              updateCachedData((draft) => {
                draft.push(row);
              });
            }
          };

          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_row_comment_stream(
                  batch_size: 1,
                  cursor: {initial_value: {created_at: "${lastMessageDate}"}, ordering: ASC},
                  where: {sheet_row_id: {_eq: ${arg.sheetRowId}}})
                ${messageType}
              }
              `,
            },
            {
              next: onNext,
              error: (err) => console.error('row comment subscription error', err),
              complete: () => {},
            }
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        }
        await cacheEntryRemoved;
        unsubscribe();
      },
      transformResponse: (response) => response.sheet_row_comment,
      providesTags: ['SheetRowComment'],
    }),
    insertSheetRowComment: insert(
      builder,
      'sheet_row_comment',
      messageType,
      afterInsert('getSheetRowComments')
    ),
    updateSheetRowComment: update(
      builder,
      'sheet_row_comment',
      messageType,
      onCommentUpdate('getSheetRowComments', true)
    ),
    getSheetRowAudit: builder.query({
      query: (params) => ({
        body: gql`
        query {
          sheet_row_audit(
            where: {sheet_id: {_eq: ${params.sheetId}}, 
            created_at: {_gte: "${new Date().toISOString()}"}}, 
            order_by: {created_at: asc}
          ) ${sheetRowAuditType}
        }
        `,
      }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // create a websocket connection when the cache subscription starts
        const { currentUser } = firebase.auth();
        // const token = await currentUser.getIdToken();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          // wait for the initial query to resolve before proceeding
          const { data: loadedData } = await cacheDataLoaded;
          const lastMessageDate =
            loadedData[loadedData.length - 1]?.created_at || new Date().toISOString();

          const onNext = (pushed) => {
            /* handle incoming values */
            const row = pushed?.data?.sheet_row_audit_stream[0];
            updateCachedData((draft) => {
              draft.push(row);
            });
          };

          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_row_audit_stream(
                  batch_size: 1,
                  cursor: {initial_value: {created_at: "${lastMessageDate}"}, ordering: ASC},
                  where: {sheet_id: {_eq: ${arg.sheetId}}, created_by: {_neq: "${currentUser.uid}"}})
                ${sheetRowAuditType}
              }
              `,
            },
            {
              next: onNext,
              error: (err) => console.error('audit subscription error', err),
              complete: () => {},
            }
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        }
        await cacheEntryRemoved;
        unsubscribe();
      },
      transformResponse: (response) => response.sheet_row_audit,
      providesTags: ['SheetRowAudit'],
    }),
    insertSheetProfile: insert(builder, 'sheet_profile', sheetProfileType),
    getSheetProfile: select(builder, 'sheet_profile', sheetProfileType, false, {
      filter: (params) =>
        `{_and: {sheet_id: {_eq: ${params.sheetId}}}, profile_id: {_eq: "${params.profileId}"}}`,
    }),
    updateSheetProfile: update(builder, 'sheet_profile', sheetProfileType),
    getSheetActiveUsers: builder.query({
      query: ({ sheetId }) => ({
        body: gql`
        query {
          sheet_active_user(where: {sheet_id: {_eq: ${sheetId}}}) ${sheetActiveUserType}
        }
        `,
      }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const { currentUser } = firebase.auth();
        const client = await ws.getClient(currentUser);
        let unsubscribe = () => {};
        try {
          await cacheDataLoaded;
          unsubscribe = client.subscribe(
            {
              query: gql`
              subscription {
                sheet_active_user(where: {sheet_id: {_eq: ${arg.sheetId}}}) ${sheetActiveUserType}
              }
              `,
            },
            {
              next: (pushed) => {
                updateCachedData(() => pushed.data.sheet_active_user);
              },
              error: (err) => console.error('active users subscription error', err),
              complete: () => {},
            }
          );
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`
        }
        await cacheEntryRemoved;
        unsubscribe();
      },
      transformResponse: (r) => r.sheet_active_user,
    }),
    upsertSheetActiveUser: builder.mutation({
      query: ({ sheetId }) => ({
        body: gql`
        mutation {
          insert_sheet_active_user_one(
            object: {sheet_id: ${sheetId}},
            on_conflict: {constraint: sheet_active_user_profile_id_sheet_id_key, update_columns: [last_seen]}
          ) { id }
        }
        `,
      }),
      transformResponse: (r) => r.insert_sheet_active_user_one,
    }),
    deleteSheetActiveUser: builder.mutation({
      query: ({ profileId, sheetId }) => ({
        body: gql`
        mutation {
          delete_sheet_active_user(where: {profile_id: {_eq: ${profileId}}, sheet_id: {_eq: ${sheetId}}}) {
            affected_rows
          }
        }
        `,
      }),
      transformResponse: (r) => r.delete_sheet_active_user,
    }),
    moveInSeq: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          move_in_seq(args: ${objectToStr(params.payload)})
          ${sheetType}
        } 
        `,
      }),
      transformResponse: (response) => response.move_in_sheet,
      invalidatesTags: (result, error, arg) => [{ type: 'Sheet', id: arg.sheet_id }],
    }),
    moveValInSeq: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          move_val_in_seq(args: ${objectToStr(params.payload)})
          ${sheetType}
        } 
        `,
      }),
      transformResponse: (response) => response.move_val_in_seq,
      onQueryStarted: afterSeqUpdate('getSheet'),
    }),
    moveManyInSeq: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          move_ids_in_seq(args: ${objectToStr(params.payload)})
          ${sheetType}
        }
        `,
      }),
      transformResponse: (response) => response.move_ids_in_seq,
      onQueryStarted: afterSeqUpdate('getSheet'),
    }),
    syncDrofus: builder.mutation({
      query: (params) => ({
        body: gql`
        mutation {
          update_drofus(where: {sheet_id: {_eq: ${params.sheetId}}}, _set: {start_sync_at: "${new Date().toISOString()}"}) {
            returning {
              id
              start_sync_at
              sheet_id
            }
          }
        }
        `,
      }),
      transformResponse: (response) => response.update_drofus.returning,
    }),
    getPrintTemplates: select(builder, 'print_template', printTemplateType, false, {
      filter: (params) =>
        `{_and: [
            {
              _or: [
                {for_company_id: {_eq: ${params.company_id}}},
                {consultancy_id: {_eq: ${params.company_id}}}
              ]
            },
            {
              _or: [
                ${params.show_archived === false ? '{is_archived: {_eq: false}}, {is_archived: {_is_null: true}}' 
                : '{is_archived: {_eq: true}}'}
              ]
            }
          ]}`,
      order: `{created_at: desc}`,
    }),
    insertPrintTemplate: insert(builder, 'print_template', printTemplateType, afterInsert('getPrintTemplates')),
    updatePrintTemplate: update(builder, 'print_template', printTemplateType, afterUpdate('getPrintTemplates')),
    copySheetRowColumn: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation {
            copy_sheet_row_column_by_sheet_id(args: {
              sheet_id: ${params.sheetId},
              source_column: "${params.sourceColumn}",
              target_column: "${params.targetColumn}"
            }) ${sheetRowType}
          }
        `,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'SheetRow', id: arg.sheetId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchSheetsQuery,
  useGetProjectSheetsQuery,
  useGetSheetQuery,
  useGetPublicSheetQuery,
  useGetLatestSheetsQuery,
  useGetSheetsQuery,
  useUpdateSheetMutation,
  useGetSubHeadersCode,
  useGetSheetRowsQuery,
  useUpdateSheetRowMutation,
  useUpdateSheetRowFieldsMutation,
  useUpdateSheetRowsMutation,
  useUpdateManySheetRowsMutation,
  useInsertSheetRowMutation,
  useInsertSheetRowsMutation,
  useGetAllSheetCommentsQuery,
  useGetSheetRowCommentsQuery,
  useGetSheetUnresolvedCommentsQuery,
  useInsertSheetRowCommentMutation,
  useUpdateSheetRowCommentMutation,
  useGetSheetRowAuditQuery,
  useCopySheetMutation,
  useGetSheetProfileQuery,
  useInsertSheetProfileMutation,
  useUpdateSheetProfileMutation,
  useMoveInSeqMutation,
  useMoveValInSeqMutation,
  useMoveManyInSeqMutation,
  useSyncDrofusMutation,
  useGetPrintTemplatesQuery,
  useInsertPrintTemplateMutation,
  useUpdatePrintTemplateMutation,
  useCopySheetRowColumnMutation,
  useGetSheetActiveUsersQuery,
  useUpsertSheetActiveUserMutation,
  useDeleteSheetActiveUserMutation,
} = sheetApi;

/*
Files to check for custom stuff:
ProductForm.js x
DescriptionModals.js x
ScheduleColumns.js x
SchedulePrint.js ?
ScheduleSheet.js x
SheetCells.js x
SheetSearch.js x
Library.js x
Schedule.js x
graphqlTypes.js x
revision.js x
sheet.js x
base.js x
*/
